import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Compass, Sparkles, Flame, Clock, Link2, Layers, CalendarClock, Map, NotebookPen, Unlock } from "lucide-react";
import { HeroSearch } from "@/components/HeroSearch";
import { TesseractIcon } from "@/components/brand/TesseractIcon";
import { TopicCard } from "@/components/TopicCard";
import { EvidenceBadge } from "@/components/EvidenceBadge";
import { CardGridSkeleton } from "@/components/Loading";
import { ProposeTopicDialog } from "@/components/ProposeTopicDialog";
import { ArtifactsFromArchive } from "@/components/ArtifactsFromArchive";
import { HomeTour } from "@/components/home/HomeTour";
import { getHome, getRandom, heroUrl } from "@/lib/api";

function Rail({ title, icon: Icon, items }) {
  const ref = useRef(null);
  if (!items?.length) return null;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  return (
    <section className="mt-14">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-serif text-2xl tracking-tight">
          {Icon ? <Icon className="h-5 w-5 text-gold-light" /> : null} {title}
        </h2>
        <div className="hidden gap-2 sm:flex">
          <button onClick={() => scroll(-1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 hover:border-gold-light/40 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => scroll(1)} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 hover:border-gold-light/40 transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={ref} className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-2">
        {items.map((t, i) => (
          <div key={t.slug} className="w-[280px] shrink-0 snap-start">
            <TopicCard topic={t} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [homeTourOpen, setHomeTourOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getHome().then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  // Auto-invite first-time visitors once (never interrupt repeatedly).
  useEffect(() => {
    let seen = true;
    try { seen = !!localStorage.getItem("codex-home-tour-seen"); } catch (e) {}
    if (!seen) {
      const t = setTimeout(() => setHomeTourOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  const closeHomeTour = (reason = "skip") => {
    setHomeTourOpen(false);
    try { localStorage.setItem("codex-home-tour-seen", "1"); } catch (e) {}
    // Gentle first-file nudge: if they finished/skipped without picking a path,
    // spotlight the fully-unsealed flagship case for an instant free read (once).
    if (reason === "skip") {
      let seen = true;
      try { seen = !!localStorage.getItem("codex-flagship-nudge-seen"); } catch (e) {}
      if (!seen) {
        try { localStorage.setItem("codex-flagship-nudge-seen", "1"); } catch (e) {}
        setTimeout(() => {
          toast("Start with an unsealed case", {
            description: "The Nephilim file is open in full — free to read, no account needed.",
            action: { label: "Open the file", onClick: () => navigate("/topic/nephilim") },
            duration: 9000,
          });
        }, 500);
      }
    }
  };

  const rabbitHole = async () => {
    try {
      const r = await getRandom();
      navigate(`/topic/${r.slug}`);
    } catch (e) {
      console.warn("rabbitHole: random topic fetch failed", e);
      toast("Couldn't open a random Case File — please try again.");
    }
  };

  return (
    <div>
      <HeroSearch />

      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8 pb-10">
        {/* First-time invitation — replayable guided tour */}
        <button
          data-testid="home-guided-tour-invite"
          onClick={() => setHomeTourOpen(true)}
          className="group mb-8 flex w-full items-center justify-between gap-4 rounded-2xl codex-glass px-5 py-4 text-left transition-colors hover:border-gold-light/40"
        >
          <span className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold-light/12 text-gold-light">
              <Compass className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[11px] uppercase tracking-[0.24em] text-gold-soft/80">New to CODEX?</span>
              <span className="block font-serif text-lg text-parchment">Take the Guided Tour</span>
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm text-gold-soft transition-transform group-hover:translate-x-0.5">
            Begin <ChevronRight className="h-4 w-4" />
          </span>
        </button>

        {/* Action bento */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <button onClick={rabbitHole} data-testid="home-random-rabbit-hole-button" className="codex-card group flex items-center gap-4 p-5 text-left">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold-light/12 text-gold-light"><Sparkles className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Go Down The Rabbit Hole</div>
              <div className="text-xs text-muted-foreground">Follow a random thread.</div>
            </div>
          </button>
          <Link to="/universe" className="codex-card group flex items-center gap-4 p-5">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#4AA3DF]/12 text-[#86C8F2]"><TesseractIcon className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Knowledge Universe</div>
              <div className="text-xs text-muted-foreground">See how everything connects.</div>
            </div>
          </Link>
          <Link to="/explore" className="codex-card group flex items-center gap-4 p-5">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#4FB286]/12 text-[#7BE3B7]"><Compass className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Explore by Category</div>
              <div className="text-xs text-muted-foreground">Explore the Archive by category.</div>
            </div>
          </Link>
          <Link to="/timelines" className="codex-card group flex items-center gap-4 p-5">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#D8B66A]/12 text-gold-light"><CalendarClock className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Timeline</div>
              <div className="text-xs text-muted-foreground">Trace history from 9600 BC to today.</div>
            </div>
          </Link>
          <Link to="/maps" className="codex-card group flex items-center gap-4 p-5">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#C07C4A]/14 text-[#E0A878]"><Map className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Atlas</div>
              <div className="text-xs text-muted-foreground">Mysteries mapped around the world.</div>
            </div>
          </Link>
          <button onClick={() => setProposeOpen(true)} data-testid="home-suggest-mystery-button" className="codex-card group flex items-center gap-4 p-5 text-left">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#8E6AD8]/16 text-[#B79BF0]"><NotebookPen className="h-6 w-6" /></span>
            <div>
              <div className="font-serif text-lg">Suggest a Mystery</div>
              <div className="text-xs text-muted-foreground">Propose a case file for the Archive.</div>
            </div>
          </button>
        </div>

        {loading ? (
          <div className="mt-14"><CardGridSkeleton count={6} /></div>
        ) : !data ? (
          <div className="mt-14 codex-glass rounded-2xl p-10 text-center">
            <h2 className="font-serif text-xl">The archive is being assembled</h2>
            <p className="mt-2 text-sm text-muted-foreground">Foundational topics are being written. Please check back shortly.</p>
          </div>
        ) : (
          <>
            {/* Today's Mystery */}
            {data.todays_mystery ? (
              <section className="mt-14">
                <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl"><Flame className="h-5 w-5 text-gold-light" /> Today&apos;s Mystery</h2>
                <Link to={`/topic/${data.todays_mystery.slug}`} className="codex-card group block overflow-hidden p-0">
                  <div className="grid grid-cols-1 md:grid-cols-5">
                    <div className="relative md:col-span-2 min-h-[180px] overflow-hidden" style={{ background: "radial-gradient(120% 120% at 20% 10%, rgba(216,182,106,0.18), rgba(20,24,38,0.4) 55%), radial-gradient(120% 120% at 90% 90%, rgba(74,163,223,0.14), rgba(11,12,16,0) 60%)" }}>
                      {data.todays_mystery.hero_image ? (
                        <>
                          <img
                            src={heroUrl(data.todays_mystery.hero_image)}
                            alt={data.todays_mystery.title}
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                          />
                          {/* scrim: blends the image down into the panel (bottom on mobile, right on desktop) */}
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1422]/75 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#0A1422]/85" />
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Layers className="h-16 w-16 text-gold-light/40" />
                        </div>
                      )}
                      {data.todays_mystery.fully_unsealed_today ? (
                        <span data-testid="fully-unsealed-badge" className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border border-gold-light/40 bg-black/55 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-gold-light backdrop-blur-sm">
                          <Unlock className="h-3 w-3" /> Fully unsealed today
                        </span>
                      ) : null}
                    </div>
                    <div className="p-6 md:col-span-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{data.todays_mystery.category}</span>
                        <EvidenceBadge level={data.todays_mystery.evidence_level} />
                      </div>
                      <h3 className="mt-2 font-serif text-2xl group-hover:text-gold-light transition-colors">{data.todays_mystery.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-muted-foreground line-clamp-4">{data.todays_mystery.summary}</p>
                    </div>
                  </div>
                </Link>
              </section>
            ) : null}

            {/* This Week's Unsealed File — a premium file, free for everyone this week */}
            {data.unsealed ? (
              <section className="mt-14" data-testid="home-unsealed">
                <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl"><Unlock className="h-5 w-5 text-gold-light" /> This Week&apos;s Unsealed File</h2>
                <Link to={`/topic/${data.unsealed.slug}`} data-testid="home-unsealed-card" className="codex-card group block overflow-hidden p-0 transition-transform duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(212,175,55,0.18)]" style={{ borderColor: "rgba(216,182,106,0.4)" }}>
                  <div className="grid grid-cols-1 md:grid-cols-5">
                    <div className="relative md:col-span-2 min-h-[180px] overflow-hidden" style={{ background: "radial-gradient(120% 120% at 20% 10%, rgba(216,182,106,0.22), rgba(20,24,38,0.4) 55%), radial-gradient(120% 120% at 90% 90%, rgba(74,163,223,0.14), rgba(11,12,16,0) 60%)" }}>
                      {data.unsealed.hero_image ? (
                        <>
                          <img src={heroUrl(data.unsealed.hero_image)} alt={data.unsealed.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1422]/75 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#0A1422]/85" />
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center"><Unlock className="h-14 w-14 text-gold-light/50" /></div>
                      )}
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--codex-gold-ink)]" style={{ backgroundColor: "#D8B66A" }}>
                        <Unlock className="h-3 w-3" /> Unsealed
                      </span>
                    </div>
                    <div className="p-6 md:col-span-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{data.unsealed.category}</span>
                        <EvidenceBadge level={data.unsealed.evidence_level} />
                        <span className="rounded-full border border-gold-light/40 bg-gold-light/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-light">Free this week</span>
                      </div>
                      <h3 className="mt-2 font-display text-2xl font-bold sm:text-3xl bg-gradient-to-br from-[#F4DE9F] via-[#D8B66A] to-[#8C6D1F] bg-clip-text text-transparent">{data.unsealed.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-muted-foreground line-clamp-3">{data.unsealed.summary}</p>
                      {data.unsealed.until ? (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/70"><Clock className="h-3 w-3" /> Full access through {new Date(data.unsealed.until).toLocaleDateString()}</p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              </section>
            ) : null}

            <Rail title="Trending in the Archive" icon={Flame} items={data.trending} />
            <Rail title="Recently Added" icon={Clock} items={data.recently_added} />

            {/* Featured collections */}
            {data.featured_collections?.length ? (
              <section className="mt-14">
                <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl"><Layers className="h-5 w-5 text-gold-light" /> Featured Dossiers</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {data.featured_collections.map((c, i) => (
                    <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="codex-card p-5">
                      <div className="font-serif text-lg">{c.name}</div>
                      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{c.description}</p>
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {c.items.slice(0, 4).map((t) => (
                          <Link key={t.slug} to={`/topic/${t.slug}`} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-muted-foreground hover:border-gold-light/40 hover:text-gold-light transition-colors">{t.title}</Link>
                        ))}
                        {c.count > 4 ? <span className="px-1 py-1 text-[11px] text-muted-foreground/70">+{c.count - 4} more</span> : null}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            ) : null}

            <Rail title="Follow the Thread" icon={Link2} items={data.recently_connected} />

            {/* Categories */}
            {data.categories?.length ? (
              <section className="mt-14">
                <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl"><Compass className="h-5 w-5 text-gold-light" /> Explore by Category</h2>
                <div className="flex flex-wrap gap-2">
                  {data.categories.map((c) => (
                    <Link key={c.name} to={`/explore?category=${encodeURIComponent(c.name)}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-2 text-sm text-muted-foreground hover:border-gold-light/40 hover:text-foreground transition-colors">
                      {c.name} <span className="text-[11px] text-muted-foreground/60">{c.count}</span>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}

        <ArtifactsFromArchive />
      </div>

      <ProposeTopicDialog open={proposeOpen} onOpenChange={setProposeOpen} />
      <HomeTour open={homeTourOpen} onClose={closeHomeTour} />
    </div>
  );
}
